/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * BusinessModelSection — pricing tiers, modules, additional revenue.
 */
import { useState } from "react";
import { motion } from "framer-motion";
import { Check, Puzzle, ChevronRight } from "lucide-react";
import PitchSection from "../PitchSection";
import SectionLabel from "../SectionLabel";
import InfoTooltip from "../InfoTooltip";

export default function BusinessModelSection({ data }) {
  const [expandModules, setExpandModules] = useState(false);

  return (
    <PitchSection id="business-model" dark>
      <SectionLabel>{data.label}</SectionLabel>
      <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-12 leading-tight">
        {data.title}
      </h2>

      {/* Pricing tiers */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
        {data.tiers.map((tier, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: i * 0.15 }}
            className={`relative rounded-2xl p-6 border transition-all duration-300 ${
              tier.highlight
                ? "bg-gradient-to-b from-[#3B82F6]/10 to-[#0F1826]/80 border-[#3B82F6]/40 shadow-lg shadow-[#3B82F6]/10"
                : "bg-[#0F1826]/60 border-white/8 hover:border-white/15"
            }`}
          >
            <h3 className="text-white font-bold text-lg mb-1">{tier.name}</h3>
            <p className="text-3xl font-extrabold text-white mb-4">
              {tier.price}
            </p>
            <ul className="space-y-2 mb-6">
              {tier.features.map((f, j) => (
                <li key={j} className="flex items-start gap-2 text-base text-gray-300">
                  <Check className="w-4 h-4 text-[#4FA9A1] mt-0.5 flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-400 italic border-t border-white/5 pt-3">
              {tier.ideal}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Modules */}
      <motion.div
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        className="bg-[#0F1826]/60 border border-white/8 rounded-2xl p-6 mb-6"
      >
        <button
          onClick={() => setExpandModules(!expandModules)}
          className="flex items-center gap-3 w-full text-left"
        >
          <Puzzle className="w-5 h-5 text-[#A78BFA]" />
          <h3 className="text-white font-semibold flex-1">Add-on Modules (mix & match)</h3>
          <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${expandModules ? "rotate-90" : ""}`} />
        </button>
        {expandModules && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2"
          >
            {data.modules.map((m, i) => (
              <div key={i} className="flex items-center gap-2 text-base text-gray-400">
                <div className="w-1.5 h-1.5 rounded-full bg-[#A78BFA]" />
                {m}
              </div>
            ))}
          </motion.div>
        )}
      </motion.div>

      {/* Additional revenue */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.additionalRevenue.map((ar, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="bg-[#0F1826]/40 border border-white/8 rounded-xl p-5"
          >
            <h4 className="text-white font-semibold text-base mb-2 flex items-center">
              {ar.title}
              {ar.tooltip && <InfoTooltip>{ar.tooltip}</InfoTooltip>}
            </h4>
            <p className="text-gray-400 text-sm leading-relaxed">{ar.body}</p>
          </motion.div>
        ))}
      </div>
    </PitchSection>
  );
}
